import { Skeleton, Text } from '@chakra-ui/react'
import { useUserState } from '~/atoms/UserState'
import { useGetCallsReport } from '~/queries/reportingQueries'
import { DateRange } from '~/types/Shared'
import { getGroupBy } from '~/utils/dashboardUtils'

import { CallsReportData, CallsReportRequest } from '@mangovoice/types'

import { toLocalDateTimeString } from '../Shared/LocalTimeHelper'
import RechartsLineChart from '../Shared/RechartsLineChart'

interface VoicemailsLineChartProps {
  dateTimeRange: DateRange
  hasModalButton: boolean
}

const VoicemailsLineChart = ({
  dateTimeRange,
  hasModalButton,
}: VoicemailsLineChartProps) => {
  const { pbxId } = useUserState()

  // Ensure the 'after' date is at least 7 days before the 'before' date
  const afterDate = dateTimeRange.after ?? new Date()
  const beforeDate = dateTimeRange.before ?? new Date()
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const { groupBy, groupByTitle } = getGroupBy(dateTimeRange)

  const callsReportRequest: CallsReportRequest = {
    filters: {
      startDate: toLocalDateTimeString(afterDate),
      endDate: toLocalDateTimeString(beforeDate),
      pbxIds: [pbxId ?? 0],
      hasVoicemail: true,
      timezone: timeZone,
    },
    metrics: [
      {
        field: 'calls',
        type: 'count',
      },
    ],
    groupBy: [groupBy],
    splitBy: [],
  }

  const {
    data: callsReport,
    isLoading: isLoading,
    error: error,
  } = useGetCallsReport({
    reportRequest: callsReportRequest,
  })

  if (isLoading) return <Skeleton height="400px" />
  if (error) return <Text>Error loading voicemail data</Text>
  if (!callsReport || !callsReport.data || callsReport.data.length === 0)
    return <Text>No voicemail data available</Text>

  const transformedCallsReport = {
    ...callsReport,
    data: callsReport.data.map((entry) => {
      const newEntry: CallsReportData = { ...entry }
      newEntry['Voicemails'] = newEntry['calls'] ?? 0
      delete newEntry['calls']
      return newEntry
    }),
  }

  return (
    <>
      <RechartsLineChart
        chartData={transformedCallsReport}
        color={['mango.orange']}
        title={`Voicemails Per ${groupByTitle}`}
        height={400}
        hasModalButton={hasModalButton}
      />
    </>
  )
}

export default VoicemailsLineChart
